import { Component, Input, OnInit, inject } from "@angular/core";
import { ReactiveFormsModule } from "@angular/forms";
import { UiKitModule } from "@app/ui-kit.module";
import { ToggleItemTextVisibilityButtonComponent } from "@components/toggle-item-text-visibility-button/toggle-item-text-visibility-button.component";
import { ToggleLockButtonComponent } from "@components/toggle-lock-button/toggle-lock-button.component";
import { ToggleVisibilityButtonComponent } from "@components/toggle-visibility-button/toggle-visibility-button.component";
import { Floorplan } from "@domain/project/floorplan/floorplan";
import { FloorplanEventType } from "@domain/project/floorplan/floorplan-event";
import { ProjectFloorplanService } from "@domain/project/project-floorplan.service";
import { UserService } from "@domain/user/user.service";
import { FloorplanMenuItemButtonsComponent } from "@project/floorplanner/floorplan-menu/fgds/floorplan-menu-item-buttons/floorplan-menu-item-buttons.component";
import { FloorplanToggleDisplayModeComponent } from "@project/floorplanner/floorplan-toggle-display-mode/floorplan-toggle-display-mode.component";
import { FloorplanToggleSelectionModeComponent } from "@project/floorplanner/floorplan-toggle-selection-mode/floorplan-toggle-selection-mode.component";
import { FloorplanService } from "@project/floorplanner/floorplan.service";
import { ProjectStateService } from "@project/project-state.service";
import { first } from "rxjs";

@Component({
  selector: "app-floorplan-main-menu",
  templateUrl: "./floorplan-main-menu.component.html",
  styleUrls: ["./floorplan-main-menu.component.scss"],
  imports: [
    UiKitModule,
    FloorplanMenuItemButtonsComponent,
    ToggleLockButtonComponent,
    ToggleVisibilityButtonComponent,
    ReactiveFormsModule,
    FloorplanToggleDisplayModeComponent,
    FloorplanToggleSelectionModeComponent,
    ToggleItemTextVisibilityButtonComponent,
  ],
})
export class FloorplanMainMenuComponent implements OnInit {
  private projectStateService = inject(ProjectStateService);
  private userService = inject(UserService);
  private floorplanService = inject(FloorplanService);
  private projectFloorplanService = inject(ProjectFloorplanService);

  @Input({ required: true }) set activeFloorplan(floorplan: Floorplan) {
    this.floorplan = floorplan;
    this.visible = this.checkIfAllVisible();
    this.itemTextVisible = this.checkIfAllItemTextsVisible();
    this.locked = this.checkIfAllLocked();
  }

  protected visible!: boolean;
  protected locked!: boolean;
  protected itemTextVisible!: boolean;
  protected floorplan!: Floorplan;

  ngOnInit(): void {
    this.projectFloorplanService.floorplanEvents$.subscribe((event) => {
      if (event.type === FloorplanEventType.FLOORPLAN_STATE_LOCK_CHANGED) {
        this.locked = this.checkIfAllLocked();
      }
      if (event.type === FloorplanEventType.FLOORPLAN_STATE_VISIBILITY_CHANGED) {
        this.visible = this.checkIfAllVisible();
      }
      if (event.type === FloorplanEventType.FLOORPLAN_STATE_ITEM_TEXT_VISIBILITY_CHANGED) {
        this.itemTextVisible = this.checkIfAllItemTextsVisible();
      }
    });
    this.userService.user$.pipe(first()).subscribe((user) => {
      if (user.readOnlyAccess) {
        this.lockUpdated(true);
      }
    });
  }

  visibilityUpdated(visible: boolean) {
    this.visible = visible;
    this.floorplan.floorplanState.setVisibilityForAll(this.visible);
  }

  lockUpdated(locked: boolean) {
    this.locked = locked;
    this.floorplan.floorplanState.setLockForAll(this.locked);
  }

  itemTextVisibilityUpdated(visible: boolean) {
    this.itemTextVisible = visible;
    this.floorplan.floorplanState.updateGlobalItemTextVisibility(this.itemTextVisible);
  }

  private checkIfAllItemTextsVisible() {
    const floorplanState = this.floorplan.floorplanState;
    return (
      floorplanState.airPathsItemTextVisible &&
      floorplanState.alarmDevicesItemTextVisible &&
      floorplanState.dangerAreasItemTextVisible &&
      floorplanState.exZonesItemTextVisible &&
      floorplanState.dustExZonesItemTextVisible &&
      floorplanState.floorplanTextsItemTextVisible &&
      floorplanState.floorplanFreeTextsItemTextVisible &&
      floorplanState.gasWarningCentersItemTextVisible &&
      floorplanState.imagesItemTextVisible &&
      floorplanState.measurementLinesItemTextVisible &&
      floorplanState.referenceMeasureItemTextVisible &&
      floorplanState.pipelinesItemTextVisible &&
      floorplanState.cableRoutesItemTextVisible &&
      floorplanState.protectiveTubesItemTextVisible &&
      floorplanState.wiresItemTextVisible &&
      floorplanState.plasticSignsItemTextVisible &&
      floorplanState.signalElementsItemTextVisible &&
      floorplanState.transmittersItemTextVisible &&
      floorplanState.patientMonitorsItemTextVisible &&
      floorplanState.centralStationsItemTextVisible &&
      floorplanState.telemetriesItemTextVisible &&
      floorplanState.kvmsItemTextVisible &&
      floorplanState.displaysItemTextVisible &&
      floorplanState.utilitiesItemTextVisible
    );
  }

  private checkIfAllVisible() {
    const floorplanState = this.floorplan.floorplanState;
    return (
      floorplanState.airPathsVisible &&
      floorplanState.alarmDevicesVisible &&
      floorplanState.dangerAreasVisible &&
      floorplanState.exZonesVisible &&
      floorplanState.dustExZonesVisible &&
      floorplanState.floorplanTextsVisible &&
      floorplanState.floorplanFreeTextsVisible &&
      floorplanState.gasWarningCentersVisible &&
      floorplanState.imagesVisible &&
      floorplanState.measurementLinesVisible &&
      floorplanState.referenceMeasureVisible &&
      floorplanState.pipelinesVisible &&
      floorplanState.cableRoutesVisible &&
      floorplanState.protectiveTubesVisible &&
      floorplanState.wiresVisible &&
      floorplanState.plasticSignsVisible &&
      floorplanState.signalElementsVisible &&
      floorplanState.transmittersVisible &&
      floorplanState.flameDetectorsVisible &&
      floorplanState.patientMonitorsVisible &&
      floorplanState.centralStationsVisible &&
      floorplanState.telemetriesVisible &&
      floorplanState.kvmsVisible &&
      floorplanState.displaysVisible &&
      floorplanState.utilitiesVisible &&
      floorplanState.isolationRoomsVisible &&
      floorplanState.itRoomsVisible &&
      floorplanState.operatingTheatresVisible &&
      floorplanState.procedureRoomsVisible &&
      floorplanState.standardRoomsVisible
    );
  }

  private checkIfAllLocked(): boolean {
    const floorplanState = this.floorplan.floorplanState;
    return (
      floorplanState.airPathsLocked &&
      floorplanState.alarmDevicesLocked &&
      floorplanState.dangerAreasLocked &&
      floorplanState.exZonesLocked &&
      floorplanState.dustExZonesLocked &&
      floorplanState.floorplanTextsLocked &&
      floorplanState.floorplanFreeTextsLocked &&
      floorplanState.gasWarningCentersLocked &&
      floorplanState.imagesLocked &&
      floorplanState.measurementLinesLocked &&
      floorplanState.referenceMeasureLocked &&
      floorplanState.pipelinesLocked &&
      floorplanState.cableRoutesLocked &&
      floorplanState.protectiveTubesLocked &&
      floorplanState.wiresLocked &&
      floorplanState.plasticSignsLocked &&
      floorplanState.signalElementsLocked &&
      floorplanState.transmittersLocked &&
      floorplanState.patientMonitorsLocked &&
      floorplanState.centralStationsLocked &&
      floorplanState.telemetriesLocked &&
      floorplanState.kvmsLocked &&
      floorplanState.displaysLocked &&
      floorplanState.utilitiesLocked &&
      floorplanState.isolationRoomsLocked &&
      floorplanState.itRoomsLocked &&
      floorplanState.operatingTheatresLocked &&
      floorplanState.procedureRoomsLocked &&
      floorplanState.standardRoomsLocked
    );
  }
}
